<script setup>
import Modal from "@/components/modal/Modal.vue";
import * as yup from "yup";
import {oneOfNumberValidator, requiredMessage, stringValidator} from "@/utils/yupValidators";
import {HpoRole, HpoRoleLabel} from "@/components/hpo/choices";
import {useDjangoForm} from "@/utils/forms";
import {useFieldArray} from "vee-validate";
import {mdiCheckCircle, mdiPlus, mdiTrashCanOutline} from "@mdi/js";
import {computed, ref} from "vue";
import {useMutation} from "@vue/apollo-composable";
import {HpoMemberSmsInviteMutation} from "@/graphql/mutations/hpo/memberInvite.graphql";

const props = defineProps({
    hpoId: {
        type: String,
        required: true,
    },
    memberTypeOptions: {
        type: Array,
        required: true
    },
});
const emit = defineEmits(['close']);

const successState = ref(false);

const schema = yup.object({
    invitations: yup.array().of(yup.object({
        phoneNumber: stringValidator.required(requiredMessage)
            .matches(/^\s*(\+420)?\s*(\d\s*){9}$/, pgettext('error', 'Phone number must have 9 digits.')),
        name: stringValidator.required(requiredMessage),
        type: oneOfNumberValidator(HpoRole),
    })),
});

const {errors, handleMutationSubmit, isSubmitting, nonFieldErrors} = useDjangoForm({
    validationSchema: schema,
    initialValues: {
        invitations: [
            {phoneNumber: '', name: '', type: props.memberTypeOptions[0]},
        ],
    },
});

const {fields: inviteFields, push: _pushInvite, remove: _removeInvite} = useFieldArray('invitations');

const canRemoveInvite = computed(() => {
    return inviteFields.value.length > 1;
});

function pushInviteField() {
    const lastRow = inviteFields.value[inviteFields.value.length - 1];

    _pushInvite({
        phoneNumber: '',
        name: '',
        type: lastRow.value.type,
    });
}

function removeInviteField(index) {
    if (!canRemoveInvite.value) {
        return;
    }
    _removeInvite(index);
}

function getInviteFieldError(index, fieldName) {
    const key = `invitations[${index}].${fieldName}`;
    return errors.value[key];
}

function close() {
    emit('close');
}

const typeOptions = computed(() => {
    return props.memberTypeOptions
        .map(type => ({value: type, title: HpoRoleLabel[type]}))
        .sort((a, b) => a.title.localeCompare(b.title, 'cs'));
});

const {mutate} = useMutation(HpoMemberSmsInviteMutation);

const onSubmit = handleMutationSubmit(
    (data) => {
        return mutate({...data, hpoId: props.hpoId});
    },
    {
        onSuccess(result) {
            successState.value = true;
        },
    }
);
</script>

<template>
    <Modal :title="pgettext('title', 'Invite by SMS')" :max-width="1000" @close="close" :closeOnClickOutside="false">
        <v-form v-if="!successState" @submit.prevent="onSubmit">
            <v-alert v-for="message in nonFieldErrors" :title="message" type="error" class="mb-8"></v-alert>

            <v-table class="member-sms-invite-table">
                <tbody>
                    <tr v-for="(field, index) in inviteFields" :key="field.key">
                        <td class="member-sms-invite-table__phone-number py-1">
                            <v-text-field :label="pgettext('label', 'Phone Number')"
                                          v-model="field.value.phoneNumber"
                                          :error-messages="getInviteFieldError(index, 'phoneNumber')" />
                        </td>
                        <td class="member-sms-invite-table__name py-1">
                            <v-text-field :label="pgettext('person_name', 'Name')"
                                          v-model="field.value.name"
                                          :error-messages="getInviteFieldError(index, 'name')" />
                        </td>
                        <td class="member-sms-invite-table__type py-1">
                            <v-select v-model="field.value.type"
                                      :label="pgettext('label', 'Type')"
                                      :error-messages="getInviteFieldError(index, 'type')"
                                      :items="typeOptions"/>
                        </td>
                        <td class="member-sms-invite-table__actions py-1">
                            <v-btn :icon="mdiTrashCanOutline" color="red" variant="tonal" :disabled="!canRemoveInvite" @click="removeInviteField(index)"/>
                        </td>
                    </tr>
                </tbody>
                <tfoot>
                    <tr>
                        <td colspan="4">
                            <v-btn :prepend-icon="mdiPlus" size="small" variant="tonal" @click="pushInviteField">
                                {{ pgettext('action', 'Add Phone Number') }}
                            </v-btn>
                        </td>
                    </tr>
                </tfoot>
            </v-table>

            <div class="mt-8">
                <v-btn block type="submit" :loading="isSubmitting">
                    {{ pgettext('action', 'Send invitations') }}
                </v-btn>
            </div>
        </v-form>
        <v-empty-state v-else color="green" :icon="mdiCheckCircle" :title="pgettext('message', 'Invitations successfully sent.')">
            <template #actions>
                <v-btn @click="close()" color="grey">
                    {{ pgettext('action', 'Close') }}
                </v-btn>
            </template>
        </v-empty-state>
    </Modal>
</template>
